import Image from 'next/image';

import { Box, Flex, Heading, List, ListItem, Text, useToken } from '@chakra-ui/react';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { HeadingWithEmphasis } from '~/components/heading-with-emphasis';
import { SanityImage } from '~/components/sanity-image';
import { SanityImageType } from '~/features/page-template/models/image';

export type PartnersSectionProps = {
  title: string;
  subtitle: string;
  description: string;
  partners: Array<{
    name: string;
    logo: SanityImageType | null;
  }>;
};

export function PartnersSection({ title, subtitle, description, partners }: PartnersSectionProps) {
  return (
    <Box
      as='section'
      bgColor='blue.800'
      color='white'
      overflow='hidden'
      position='relative'
      padding={{
        base: '40px 0px',
        md: '80px 0px',
      }}
    >
      <LinesBackground />
      <ContentContainer
        display='flex'
        position='relative'
        flexDir={{
          base: 'column',
          md: 'row',
        }}
        gap={{
          base: 6,
          md: 8,
        }}
      >
        <Flex
          flexDir='column'
          flex={{
            md: '0 0 254px',
          }}
        >
          <Heading
            as='h2'
            mb={{
              base: 6,
              md: 8,
            }}
            size={{
              base: 'marketing-3xl',
              md: 'marketing-4xl',
            }}
            textAlign={{
              base: 'center',
              md: 'left',
            }}
          >
            {title}
          </Heading>
          <Card
            backgroundColor='blue.400'
            border='none'
            padding={{
              base: 4,
              md: '16px 24px',
            }}
          >
            <HeadingWithEmphasis
              text={subtitle}
              emphasisProps={{
                fontSize: {
                  base: '2rem',
                  md: '2.5rem',
                },
                lineHeight: {
                  base: '2.6rem',
                  md: '3rem',
                },
                fontWeight: '500',
              }}
              headingProps={{
                as: 'p',
                size: {
                  base: 'marketing-lg',
                  md: 'marketing-xl',
                },
                whiteSpace: {
                  md: 'pre-wrap',
                },
                mb: 2,
                display: {
                  base: 'flex',
                  md: 'unset',
                },
                alignItems: {
                  base: 'center',
                  md: 'unset',
                },
                gap: {
                  base: 2,
                  md: 'unset',
                },
              }}
            />
            <Text>{description}</Text>
          </Card>
        </Flex>
        <List
          display='grid'
          flex='1'
          gap={2}
          gridTemplateColumns='repeat(4, 1fr)'
          gridAutoRows={{
            base: '60px',
            md: '120px',
          }}
        >
          {partners.map((partner, idx) => (
            <ListItem key={idx} display='flex'>
              <Card
                alignItems='center'
                border='none'
                display='flex'
                flex='1'
                justifyContent='center'
                p={4}
                sx={{
                  '& > img': {
                    maxHeight: {
                      base: '46px',
                      md: '85.54px',
                    },
                  },
                }}
              >
                {partner.logo && (
                  <SanityImage
                    alt={partner.name}
                    image={partner.logo}
                    style={{
                      objectFit: 'contain',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Card>
            </ListItem>
          ))}
        </List>
      </ContentContainer>
    </Box>
  );
}

function LinesBackground() {
  const [contentMaxWidthMd] = useToken('sizes', ['content-max-width-md']);
  return (
    <Box
      left={`calc((100vw - ${contentMaxWidthMd})/2)`}
      opacity={0.5}
      pointerEvents='none'
      position='absolute'
      top='40%'
      transform='translate(-30%, -50%)'
    >
      <Box height='600px' position='relative' width='2440px'>
        <Image
          alt=''
          quality={100}
          sizes='2440px'
          src='/images/backgrounds/blue-lines-wide.png'
          style={{
            objectFit: 'cover',
            objectPosition: 'bottom',
            filter: 'brightness(0) invert(1)',
          }}
          fill
        />
      </Box>
    </Box>
  );
}
